import { useEffect } from 'react';
import { View, Pressable, Text, Platform, useWindowDimensions, StatusBar } from 'react-native';
import { slingshotViewStyles as styles, fullscreenStyles } from './SlingshotView.styles';
import { SlingshotWebView } from './SlingshotWebView';
import { SlingshotOverlays } from './SlingshotOverlays';
import { useSlingshotGame } from '../../../src/quiz/hooks/useSlingshotGame';
import { SLINGSHOT_TEST_IDS } from '../../../src/quiz/constants/slingshot.constants';

interface SlingshotViewProps {
  onMoreGames: () => void;
}

export function SlingshotView({ onMoreGames }: SlingshotViewProps) {
  const game = useSlingshotGame();
  const { width, height } = useWindowDimensions();
  const isPortrait = height > width;

  // Hide status bar on native when game is open
  useEffect(() => {
    if (Platform.OS !== 'web') StatusBar.setHidden(true, 'fade');
    return () => { if (Platform.OS !== 'web') StatusBar.setHidden(false, 'fade'); };
  }, []);

  // Web: request fullscreen on mount
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const el = document.documentElement;
    try { el.requestFullscreen?.(); } catch { /* fullscreen not supported */ }
    return () => { try { document.exitFullscreen?.(); } catch { /* not in fullscreen */ } };
  }, []);

  // Back mid-game opens the session summary; before play starts (loading/error) or after the campaign
  // overlay is already up, just leave straight away.
  const handleBack = () => {
    if (game.status === 'ready' && !game.completed) game.requestExit();
    else onMoreGames();
  };
  const handleLeave = () => {
    game.confirmExit();
    onMoreGames();
  };

  // On portrait phones: rotate the entire game 90° to fill the screen in landscape
  const rotateStyle = isPortrait ? {
    width: height,
    height: width,
    transform: [{ rotate: '90deg' }, { translateX: (height - width) / 2 }, { translateY: (height - width) / 2 }],
  } : { flex: 1 as const };

  return (
    <View style={[styles.fullscreenContainer, rotateStyle]} testID={SLINGSHOT_TEST_IDS.view}>
      {/* Back arrow — always top-left, on top of everything */}
      <Pressable
        style={({ pressed }) => [fullscreenStyles.backButton, pressed && styles.pressedDim]}
        onPress={handleBack}
      >
        <Text style={fullscreenStyles.backArrow}>←</Text>
      </Pressable>

      <View style={styles.canvasWrap}>
        <SlingshotWebView
          ref={(r) => game.bindWebView(r)}
          html={game.html}
          onMessage={game.onMessage}
        />
      </View>

      <SlingshotOverlays
        status={game.status}
        errorMessage={game.errorMessage}
        coinsLeftToday={game.coinsLeftToday}
        capReached={game.capReached}
        bestLevel={game.bestLevel}
        coinsBeat={game.coinsBeat}
        newBest={game.newBest}
        completed={game.completed}
        finalLevel={game.finalLevel}
        toast={game.toast}
        showExitSummary={game.showExitSummary}
        levelReached={game.levelReached}
        levelsCleared={game.levelsCleared}
        sessionCoins={game.sessionCoins}
        milestone={game.milestone}
        onRetry={game.retry}
        onPlayAgain={game.onPlayAgain}
        onMoreGames={onMoreGames}
        onLeave={handleLeave}
        onKeepPlaying={game.dismissExitSummary}
        onMilestoneContinue={game.dismissMilestone}
      />
    </View>
  );
}
